import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Lock, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { gbp } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Notice } from "@/components/Badges";

const CardPreview = () => (
  <div className="space-y-3" aria-hidden data-testid="test-checkout-card-preview">
    <div><p className="label">Card number</p><div className="input flex items-center justify-between text-slate-400">4242 4242 4242 4242 <CreditCard className="h-4 w-4" /></div></div>
    <div className="grid grid-cols-2 gap-3"><div><p className="label">Expiry</p><div className="input text-slate-400">12 / 34</div></div><div><p className="label">CVC</p><div className="input text-slate-400">123</div></div></div>
  </div>
);

export default function TestCheckout() {
  const { orgId } = useAuth();
  const [params] = useSearchParams();
  const sid = params.get("session");
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (orgId && sid) api.get(`/orgs/${orgId}/billing/test-checkout/${sid}`).then(({ data }) => setD(data)).catch((e) => setErr(errMsg(e)));
  }, [orgId, sid]);
  const complete = async () => {
    setBusy(true);
    try { await api.post(`/orgs/${orgId}/billing/test-checkout/${sid}/complete`); toast.success("You're signed up. Full platform access is on."); nav("/app/billing"); } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (err) return <Notice tone="danger" testid="test-checkout-error">{err}</Notice>;
  if (!d) return <p className="text-sm text-slate-500">Loading…</p>;
  return (
    <div className="fade-up mx-auto grid max-w-4xl gap-6 md:grid-cols-2" data-testid="test-checkout-page">
      <section className="card p-6">
        <p className="caption">Order summary</p>
        <h1 className="mt-2 text-2xl font-bold text-[#0B192C]" data-testid="test-checkout-plan">{d.plan_name}</h1>
        <p className="mt-1 text-3xl font-bold text-[#0B192C]" data-testid="test-checkout-amount">{gbp(d.amount)}<span className="text-sm font-normal text-slate-500"> / {d.interval === "year" ? "year" : "month"}</span></p>
        <ul className="mt-4 space-y-2 text-sm text-slate-700">{d.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 text-[#1F8A8B]" />{f}</li>)}</ul>
        <p className="mt-4 text-xs text-slate-500">Billed to {d.email}. Cancel any time.</p>
      </section>
      <section className="card space-y-4 p-6">
        <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#1F8A8B]" /><h2 className="font-semibold text-[#0B192C]">Payment</h2></div>
        <Notice tone="warn" testid="test-checkout-notice">Testing version: this shows what the payment step will look like. No card details are collected and nothing is charged.</Notice>
        <CardPreview />
        <button onClick={complete} disabled={busy || d.status !== "initiated"} className="btn-teal w-full justify-center" data-testid="test-checkout-complete">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{d.status === "initiated" ? `Continue without paying · ${gbp(d.amount)}` : "Already completed"}</button>
        <Link to="/app/billing" className="block text-center text-sm text-slate-500 underline" data-testid="test-checkout-back">Back to plans</Link>
      </section>
    </div>
  );
}
